import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { groupAPI, notificationAPI, formatApiError } from '../services/api';
import { PlusIcon, ArrowRightIcon, UserGroupIcon, BellIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import CreateGroupModal from '../components/CreateGroupModal';
import NotificationPanel from '../components/NotificationPanel';
import { toast } from 'sonner';
import { formatTimeAgo } from '../utils/dateUtils';
import InstallPrompt from '../components/InstallPrompt';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [summary, setSummary] = useState({ piutang: 0, hutang: 0 });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGroups();
    fetchNotificationCount();
  }, []);

  const fetchGroups = async () => {
    try {
      const response = await groupAPI.getGroups();
      setGroups(response.data.groups || []);
      setSummary(response.data.summary || { piutang: 0, hutang: 0 });
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const fetchNotificationCount = async () => {
    try {
      const response = await notificationAPI.getNotifications();
      setUnreadCount(response.data.unread_count || 0);
    } catch (e) { /* silent */ }
  };

  const handleCreateGroup = async (groupData) => {
    try {
      await groupAPI.createGroup(groupData);
      toast.success('Grup berhasil dibuat!');
      fetchGroups();
      setShowCreateModal(false);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency', currency: 'IDR', minimumFractionDigits: 0, maximumFractionDigits: 0
    }).format(amount);
  };



  return (
    <div className="min-h-screen bg-white pb-24">
      {/* Header */}
      <div className="bg-surface px-6 pt-12 pb-8 rounded-b-[32px] mb-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <img
              src={`https://api.dicebear.com/9.x/micah/svg?seed=${user?.name || 'User'}`}
              alt="Avatar"
              className="w-12 h-12 rounded-full border-2 border-white shadow-sm"
            />
            <div>
              <h2 className="font-semibold text-slate-900">Halo, {user?.name || 'Pengguna'}!</h2>
              <p className="text-sm text-slate-600">Selamat datang kembali</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowNotifications(true)}
              data-testid="notification-btn"
              className="relative w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm"
            >
              <BellIcon className="w-5 h-5 text-slate-700" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-danger text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
            <button onClick={logout} data-testid="logout-btn" className="text-sm text-slate-600 hover:text-slate-900 font-medium">
              Logout
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-4">
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.1 }}
            className="bg-primary text-white p-5 rounded-3xl shadow-lg relative overflow-hidden" data-testid="piutang-card">
            <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-8 -mt-8"></div>
            <p className="text-sm opacity-90 mb-1">Total Piutang</p>
            <p className="text-2xl font-bold">{formatCurrency(summary.piutang)}</p>
            <p className="text-xs opacity-75 mt-2">Uang yang akan kembali</p>
          </motion.div>
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.2 }}
            className="bg-white p-5 rounded-3xl border border-border shadow-sm" data-testid="hutang-card">
            <p className="text-sm text-slate-600 mb-1">Total Hutang</p>
            <p className="text-2xl font-bold text-slate-900">{formatCurrency(summary.hutang)}</p>
            <p className="text-xs text-slate-500 mt-2">Uang yang harus dibayar</p>
          </motion.div>
        </div>
      </div>

      {/* Groups Section */}
      <div className="px-6">
        <h3 className="text-xl font-semibold text-slate-900 mb-4">Grup Aktif</h3>
        {loading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
        ) : groups.length === 0 ? (
          <div className="text-center py-12">
            <UserGroupIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600 mb-2">Belum ada grup.</p>
            <p className="text-sm text-slate-500">Yuk buat grup pertamamu!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {groups.map((group, idx) => (
              <motion.div key={group.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.1 }}
                onClick={() => navigate(`/group/${group.id}`)} data-testid={`group-item-${group.id}`}
                className="flex items-center justify-between p-4 bg-white border border-border rounded-3xl hover:bg-surface transition-colors cursor-pointer shadow-sm">
                <div className="flex items-center gap-4">
                  <div className="flex -space-x-3 avatar-stack">
                    {group.members.slice(0, 3).map((member, i) => (
                      <img key={i} src={`https://api.dicebear.com/9.x/micah/svg?seed=${member}`} alt={member} className="w-10 h-10 rounded-full" />
                    ))}
                    {group.members.length > 3 && (
                      <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-xs font-semibold text-slate-600 border-2 border-white">
                        +{group.members.length - 3}
                      </div>
                    )}
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">{group.name}</h4>
                    <p className="text-xs text-slate-500">{formatTimeAgo(group.lastActivity)}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`font-bold ${group.balance >= 0 ? 'text-success' : 'text-danger'}`}>
                    {group.balance >= 0 ? '+' : ''}{formatCurrency(group.balance)}
                  </p>
                  <ArrowRightIcon className="w-4 h-4 text-slate-400 ml-auto" />
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* FAB */}
      <motion.button initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.5, type: 'spring' }}
        onClick={() => setShowCreateModal(true)} data-testid="create-group-fab"
        className="fixed bottom-20 right-6 w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center shadow-float hover:scale-105 active:scale-95 transition-transform z-50">
        <PlusIcon className="w-8 h-8" />
      </motion.button>

      {showCreateModal && <CreateGroupModal onClose={() => setShowCreateModal(false)} onSubmit={handleCreateGroup} />}
      {showNotifications && <NotificationPanel onClose={() => { setShowNotifications(false); fetchNotificationCount(); }} />}
      <InstallPrompt />
    </div>
  );
};

export default Dashboard;
